import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, Users, Briefcase, AlertTriangle, Plus, Check, Shield, ChevronRight, LogOut, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { TeamQuickClock } from '../components/TeamQuickClock';
import SupervisorWidgets from '../components/approvals/SupervisorWidgets';
import { enqueuePunch, isNetworkError } from '../utils/offlineQueue';
import MealBreakDialog from '../components/MealBreakDialog';
import ClockOutReceipt from '../components/ClockOutReceipt';
import EmptyState from '../components/EmptyState';

const API = `${window.__BACKEND_URL__}/api`;

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, loading: authLoading } = useAuth();
  const [clockStatus, setClockStatus] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState('0h 0m');
  const [showCreateAnn, setShowCreateAnn] = useState(false);
  const [newAnn, setNewAnn] = useState({ title: '', body: '', priority: 'info' });

  const fetchAll = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [statusRes, annRes, usersRes, jobsRes] = await Promise.all([
        axios.get(`${API}/clock-in/status`, { headers: h }),
        axios.get(`${API}/safety-announcements`, { headers: h }),
        axios.get(`${API}/users?scope=all`, { headers: h }),
        axios.get(`${API}/jobs`, { headers: h })
      ]);
      setClockStatus(statusRes.data);
      setAnnouncements(annRes.data);
      setTeamMembers(usersRes.data.filter(u => u.role === 'user'));
      setJobs(jobsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  useEffect(() => {
    if (!clockStatus?.clocked_in) return;
    const timer = setInterval(() => {
      const start = new Date(clockStatus.clock_in_time);
      const diff = Math.floor((Date.now() - start.getTime()) / 1000);
      setElapsed(`${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`);
    }, 1000);
    return () => clearInterval(timer);
  }, [clockStatus]);


  const [mealPrompt, setMealPrompt] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const finishAndLeave = () => { logout(); navigate('/'); };

  const handleClockOut = async (deductBreak = false, confirmNoBreak = false) => {
    if (!deductBreak && !confirmNoBreak && !window.confirm(`Clock out? You've worked ${elapsed}.`)) return;
    try {
      const res = await axios.post(
        `${API}/clock-out/quick`,
        { deduct_break: deductBreak, confirm_no_break: confirmNoBreak },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.meal_break_warning) {
        setMealPrompt({ shift_hours: res.data.shift_hours });
        return;
      }
      setMealPrompt(null);
      setReceipt({ hours: res.data.hours_worked, deducted: res.data.meal_break_deducted });
    } catch (err) {
      if (isNetworkError(err)) {
        enqueuePunch('clock_out', {});
        alert('No signal — your clock-out was saved on this device and will sync automatically once you\'re back online.');
        finishAndLeave();
        return;
      }
      console.error('Supervisor quick clock-out failed', err);
      alert('Could not clock out. Please try again or use the full Clock Out page.');
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/safety-announcements`, newAnn, { headers: { Authorization: `Bearer ${token}` } });
      setShowCreateAnn(false);
      setNewAnn({ title: '', body: '', priority: 'info' });
      fetchAll();
    } catch (err) {
      console.error('Create safety announcement failed', err);
      alert('Could not publish announcement. Please try again.');
    }
  };

  const handleArchiveAnn = async (id) => {
    await axios.patch(`${API}/safety-announcements/${id}`, { active: false }, { headers: { Authorization: `Bearer ${token}` } });
    fetchAll();
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1E88E5' }} /></div>;
  }

  const activeJobs = jobs.filter(j => !['completed', 'archived'].includes(j.status) && !j.deleted_at);
  const overdueJobs = jobs.filter(j => j.scheduled_completion && new Date(j.scheduled_completion) < new Date() && j.status !== 'completed');

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="supervisor-dashboard">
      <MealBreakDialog
        open={!!mealPrompt}
        shiftHours={mealPrompt?.shift_hours}
        onDeduct={() => handleClockOut(true, false)}
        onNoBreak={() => handleClockOut(false, true)}
        onCancel={() => setMealPrompt(null)}
      />
      <ClockOutReceipt receipt={receipt} onClose={finishAndLeave} autoCloseMs={3000} />
      {/* Status Bar */}
      <div className="ht-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.15)' }}>
            <Clock className="w-5 h-5" style={{ color: '#00C896' }} />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Clocked In &bull; {elapsed}</p>
            <p className="text-xs" style={{ color: '#64748B' }}>Since {clockStatus?.clocked_in ? new Date(clockStatus.clock_in_time).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '--'}</p>
          </div>
        </div>
        <button onClick={() => handleClockOut()} className="px-3 py-2 rounded-xl text-xs font-medium" style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="sup-clock-out">
          <LogOut className="w-3 h-3 inline mr-1" /> Clock Out
        </button>
      </div>

      {/* Timekeeping overview widgets */}
      <SupervisorWidgets token={token} />

      {/* Quick team clock in/out */}
      <TeamQuickClock onPunch={fetchAll} />


      {/* Safety Management */}
      <div className="ht-card p-5" data-testid="safety-management">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Shield className="w-4 h-4" style={{ color: '#FFA000' }} /> Safety Announcements</h3>
          <button onClick={() => setShowCreateAnn(true)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white ht-btn-glow" style={{ background: '#1E88E5' }}>
            <Plus className="w-3 h-3 inline mr-1" /> Create
          </button>
        </div>
        {showCreateAnn && (
          <form onSubmit={handleCreateAnnouncement} className="mb-4 p-4 rounded-xl space-y-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
            <input value={newAnn.title} onChange={e => setNewAnn({ ...newAnn, title: e.target.value })} placeholder="Title" required className="w-full px-3 py-2 rounded-lg text-sm text-white" style={{ background: '#1A2433', border: '1px solid #2D3748' }} />
            <textarea value={newAnn.body} onChange={e => setNewAnn({ ...newAnn, body: e.target.value })} placeholder="Announcement body..." required rows={3} className="w-full px-3 py-2 rounded-lg text-sm text-white" style={{ background: '#1A2433', border: '1px solid #2D3748' }} />
            <select value={newAnn.priority} onChange={e => setNewAnn({ ...newAnn, priority: e.target.value })} className="px-3 py-2 rounded-lg text-sm text-white" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
              <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
            </select>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: '#1E88E5' }}>Publish</button>
              <button type="button" onClick={() => setShowCreateAnn(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}>Cancel</button>
            </div>
          </form>
        )}
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {announcements.length > 0 ? announcements.map(ann => (
            <div key={ann.id} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: '#0F1923' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{ann.title}</p>
                <p className="text-xs" style={{ color: '#64748B' }}>{ann.acknowledgement_count} acknowledged &bull; {ann.priority}</p>
              </div>
              <button onClick={() => handleArchiveAnn(ann.id)} className="text-xs px-2 py-1 rounded" style={{ color: '#EF4444' }}><X className="w-3 h-3" /></button>
            </div>
          )) : <p className="text-sm text-center py-4" style={{ color: '#64748B' }}>No active announcements</p>}
        </div>
      </div>

      {/* Team Overview */}
      <div className="ht-card p-5" data-testid="team-overview">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4" style={{ color: '#1E88E5' }} /> Team ({teamMembers.length})</h3>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {teamMembers.map(m => (
            <div key={m.id} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: '#0F1923' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: '#2D3748' }}>{m.name.charAt(0)}</div>
                <p className="text-sm text-white">{m.name}</p>
              </div>
              <span className="ht-pill ht-pill-gray text-xs">Staff</span>
            </div>
          ))}
          {teamMembers.length === 0 && (
            <EmptyState
              icon={Users}
              title="No team members yet"
              description="Assign workers to your team to see them here."
              action="Manage Teams"
              onAction={() => navigate('/teams')}
            />
          )}
        </div>
      </div>

      {/* Job Management */}
      <div className="ht-card p-5" data-testid="job-management">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4" style={{ color: '#00C896' }} /> Jobs ({activeJobs.length})</h3>
          <button onClick={() => navigate('/jobs')} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white ht-btn-glow" style={{ background: '#1E88E5' }}>
            <Plus className="w-3 h-3 inline mr-1" /> New Job
          </button>
        </div>
        {overdueJobs.length > 0 && (
          <div className="mb-3 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <p className="text-sm font-medium" style={{ color: '#EF4444' }}><AlertTriangle className="w-4 h-4 inline mr-1" /> {overdueJobs.length} overdue job{overdueJobs.length !== 1 ? 's' : ''}</p>
          </div>
        )}
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {activeJobs.slice(0, 10).map(job => (
            <button key={job.id} onClick={() => navigate('/jobs')} className="w-full flex items-center justify-between py-2 px-3 rounded-lg text-left hover:bg-white/5 transition-all" style={{ background: '#0F1923' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{job.title}</p>
                <p className="text-xs" style={{ color: '#64748B' }}>{job.assigned_to_name || 'Unassigned'}</p>
              </div>
              <span className={`ht-pill text-xs ${job.status === 'in_progress' ? 'ht-pill-blue' : job.status === 'paused' ? 'ht-pill-amber' : 'ht-pill-gray'}`}>
                {job.status === 'in_progress' ? 'Active' : job.status}
              </span>
            </button>
          ))}
          {activeJobs.length === 0 && <p className="text-sm text-center py-4" style={{ color: '#64748B' }}>No active jobs</p>}
        </div>
      </div>
    </div>
  );
}

